import type { AuditRecorder } from "../audit/audit-recorder.js";
import type { DocumentVisual, TemplatedDocument } from "../domain/studio/visual-design.js";
import type { StudioDesignRepository } from "../repositories/studio-design-repository.js";
import type { DocumentStorageService } from "../application/services/document-storage-service.js";
import { StudioDesignProvider } from "../application/services/studio-design-provider.js";
import { StudioDesignService } from "../application/services/studio-design-service.js";

export interface StudioDesignWiringTargets {
  /** ME, LP, PM, US-Convencional, B-Scan e AVR — os que imprimem pelo cabeçalho compartilhado. */
  readonly documentServices: ReadonlyArray<{ attachDocumentVisual(provider: (type: TemplatedDocument) => Promise<DocumentVisual>): void }>;
  readonly userName: (userId: string) => Promise<string | null>;
  /** RI: carimbo da versão visual na aprovação e logo pela versão carimbada. */
  readonly integrityReports?: { attachVisualHooks(hooks: { stamp(reportId: string): Promise<unknown>; logo(reportId: string, closed: boolean): Promise<string | null> }): void };
  /** Minuta: logo publicado congelado no snapshot de cada revisão. */
  readonly minutaEngineering?: { attachDocumentLogo(provider: () => Promise<string | null>): void };
  /**
   * Book de Inspeção — mesmo padrão do RI (sem modelo congelado): o visual é
   * CARIMBADO na emissão (`InspectionBookService.issueBook`) e o consolidador
   * lê sempre pela versão carimbada. Book emitido sem carimbo (anterior ao
   * Studio) = V0; Book aberto/preliminar (ainda não emitido) = versão vigente
   * publicada — muda a cada publicação, como o preview já mostra.
   */
  readonly inspectionBookService?: { attachVisualStamp(stamp: (bookId: string) => Promise<unknown>): void };
  readonly inspectionBookConsolidator?: { attachDocumentVisual(provider: (bookId: string, closed: boolean) => Promise<DocumentVisual>): void };
}

/**
 * Liga o STUDIO INSPECT IA aos módulos. A mesma função nas duas composições
 * (navegador e servidor). Os arquivos vão para o armazenamento oficial
 * (`DocumentStorageService`), nunca para dentro do desenho.
 */
export function wireStudioDesign(
  repo: StudioDesignRepository,
  storage: DocumentStorageService,
  audit: AuditRecorder | undefined,
  targets: StudioDesignWiringTargets
): { provider: StudioDesignProvider; service: StudioDesignService } {
  const provider = new StudioDesignProvider(repo, storage);
  const service = new StudioDesignService(provider, repo, storage, audit);
  service.attachUserNameResolver(targets.userName);
  for (const s of targets.documentServices) s.attachDocumentVisual(type => provider.documentVisual(type));
  targets.integrityReports?.attachVisualHooks({
    stamp: reportId => provider.stampDocument("RI", reportId),
    logo: async (reportId, closed) => (await provider.visualForDocument("RI", reportId, closed)).logoImage
  });
  targets.minutaEngineering?.attachDocumentLogo(async () => (await provider.documentVisual("RI")).logoImage);
  targets.inspectionBookService?.attachVisualStamp(bookId => provider.stampDocument("BOOK", bookId));
  targets.inspectionBookConsolidator?.attachDocumentVisual((bookId, closed) => provider.visualForDocument("BOOK", bookId, closed));
  return { provider, service };
}
